<template>
    <div>
        <b-row class="mb-2">
            <b-col class="d-flex" style="gap: 8px;">
                <b-button :variant="pdfSource ? 'info' : 'outline-info'" @click="viewReport" :disabled="isLoadingReport || isDownloading">
                    <i v-if="isLoadingReport" class="fa fa-spinner fa-spin fa-fw mr-1"></i>
                    <i v-else :class="{ 'fa fa-chevron-down fa-fw mr-1': true, 'fa-rotate-180': !pdfSource }" style="transition: all .2s;"></i>
                    {{ isLoadingReport ? 'Loading...' : (pdfSource ? 'Hide Report' : 'View Report') }}
                </b-button>
                <b-button variant="outline-secondary" @click="downloadPdf" :disabled="isDownloading || isLoadingReport">
                    <i v-if="isDownloading" class="fa fa-spinner fa-spin fa-fw mr-1"></i>
                    <i v-else class="fa fa-download fa-fw mr-1"></i>
                    {{ isDownloading ? 'Downloading...' : 'Download PDF' }}
                </b-button>
            </b-col>
        </b-row>
        <b-row v-if="pdfSource" class="mb-3">
            <b-col>
                <b-embed type="embed" :src="pdfSource" style="width: 100%; height: 60vh;" />
            </b-col>
        </b-row>

        <hr />
        <h6>Fax to Provider</h6>
        <b-row>
            <b-col cols="12" md="6">
                <b-form-group label="Provider on file">
                    <b-form-select :options="providerOptions"
                        :disabled="isLoadingProviders" @change="onProviderSelected" />
                </b-form-group>
            </b-col>
            <b-col cols="12" md="6">
                <b-form-group label="Fax Number">
                    <b-input v-model="faxNumber" placeholder="Select a provider or enter a fax number" />
                </b-form-group>
            </b-col>
        </b-row>
        <b-row>
            <b-col class="text-right">
                <b-button variant="primary" @click="sendFax" :disabled="!validFax || isSending">
                    <i v-if="isSending" class="fa fa-spinner fa-spin fa-fw mr-1"></i>
                    Send Fax
                </b-button>
            </b-col>
        </b-row>
    </div>
</template>

<script lang="ts">
import { Component, Vue, Prop } from 'vue-property-decorator';
import axios, { CancelTokenSource } from 'axios';
import { Patient } from '../../models/Patient';
import { Prescriber } from '../../models/Prescriber';
import { NotificationOptions } from '@/util/NotificationOptionsPresets';
import TextUtils from '@/util/TextUtils';

@Component({
    name: "MedicationListReportComponent",
})
export default class MedicationListReportComponent extends Vue {
    @Prop() patient!: Patient;

    private pdfSource: string | null = null;
    private cancelSource: CancelTokenSource | null = null;
    private isLoadingReport = false;
    private isDownloading = false;
    private isSending = false;
    private isLoadingProviders = false;
    private faxNumber = "";
    private providers: Prescriber[] = [];

    created() {
        this.loadProviders();
    }

    get providerOptions() {
        const options = this.providers
            .filter(p => !!p.faxNumber && p.faxNumber.trim().length > 0)
            .map(p => ({
                value: p.faxNumber,
                text: `${p.lastName ?? ''}, ${p.firstName ?? ''} — ${p.faxNumber}`.trim(),
            }));
        return [{ value: null, text: '-- Select a provider --' }, ...options];
    }

    get validFax(): boolean {
        return (this.faxNumber ?? '').replace(/\D/g, '').length >= 10;
    }

    onProviderSelected(fax: string | null) {
        if (fax) this.faxNumber = fax;
    }

    async loadProviders() {
        try {
            this.isLoadingProviders = true;
            const response = await axios.get<Prescriber[]>(`/Prescriber/by-patient/${this.patient.id}`);
            this.providers = response.data ?? [];
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        } finally {
            this.isLoadingProviders = false;
        }
    }

    async viewReport() {
        if (this.isLoadingReport) return;

        if (this.pdfSource) {
            // Toggle off - release the object URL and clear the preview.
            URL.revokeObjectURL(this.pdfSource);
            this.pdfSource = null;
            return;
        }

        this.cancelSource = axios.CancelToken.source();
        try {
            this.isLoadingReport = true;
            const url = `reports/medicationList/${this.patient.id}`;
            const response = await axios.get(url, { responseType: "blob", cancelToken: this.cancelSource.token });
            this.pdfSource = URL.createObjectURL(response.data);
        } catch (err) {
            if (!axios.isCancel(err)) this.$notification(NotificationOptions.error(err));
        } finally {
            this.isLoadingReport = false;
            this.cancelSource = null;
        }
    }

    async downloadPdf() {
        if (this.isDownloading) return;
        try {
            this.isDownloading = true;
            const url = `reports/medicationList/${this.patient.id}`;
            const response = await axios.get(url, { responseType: "blob" });
            const objectUrl = URL.createObjectURL(response.data);
            const link = document.createElement('a');
            link.href = objectUrl;
            link.download = `MedicationList_${this.patient.lastName}_${this.patient.firstName}.pdf`;
            link.click();
            URL.revokeObjectURL(objectUrl);
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        } finally {
            this.isDownloading = false;
        }
    }

    async sendFax() {
        if (this.isSending) return;

        try {
            this.isSending = true;
            await axios.post(`reports/medicationList/${this.patient.id}/fax`, {
                faxNumber: TextUtils.formatFaxNumber(this.faxNumber),
            });
            this.$notification(NotificationOptions.success("Medication list faxed successfully."));
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        } finally {
            this.isSending = false;
        }
    }

    beforeDestroy() {
        this.cancelSource?.cancel();
        if (this.pdfSource) URL.revokeObjectURL(this.pdfSource);
    }
}
</script>
